"use client";

import {
	type OnChangeFn,
	type PaginationState,
	type SortingState,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	DEFAULT_PAGE_SIZE,
	offsetListParams,
	pageCountFromTotal,
	SEARCH_DEBOUNCE_MS,
	type OffsetListParams,
	type TableSnapshot,
} from "./offset-list-params";
import { parseTableUrlState, tableUrlSearch } from "./table-url-state";

export type { OffsetListParams, TableSnapshot } from "./offset-list-params";
export type { CursorListParams } from "./offset-list-params";
export type { TableUrlDefaults } from "./table-url-state";
export { parseTableUrlState, TABLE_URL_KEYS, tableUrlSearch } from "./table-url-state";
export {
	clampPageSize,
	cursorListParams,
	decodeOffsetCursor,
	DEFAULT_PAGE_SIZE,
	encodeOffsetCursor,
	hasNextPage,
	MAX_PAGE_SIZE,
	offsetListParams,
	pageCountFromTotal,
	PAGE_SIZE_OPTIONS,
	rowRange,
	SEARCH_DEBOUNCE_MS,
} from "./offset-list-params";

export type ServerTableBind = {
	sorting: SortingState;
	onSortingChange: OnChangeFn<SortingState>;
	globalFilter: string;
	onGlobalFilterChange: (value: string) => void;
	pagination: PaginationState;
	onPaginationChange: OnChangeFn<PaginationState>;
	rowCount: number;
	pageCount: number;
	isFetching?: boolean;
};

export interface ServerTableState<TParams> {
	sorting: SortingState;
	onSortingChange: OnChangeFn<SortingState>;
	globalFilter: string;
	onGlobalFilterChange: (value: string) => void;
	pagination: PaginationState;
	onPaginationChange: OnChangeFn<PaginationState>;
	pageSize: number;
	params: TParams;
	tableProps: (rowCount: number, extras?: { isFetching?: boolean }) => ServerTableBind;
}

export interface ServerTableStateOptions<TParams> {
	pageSize?: number;
	debounceMs?: number;
	resetKey?: string | number;
	toParams?: (state: TableSnapshot) => TParams;
	/**
	 * Mirror page/pageSize/sort/order/q into the URL query string (and restore
	 * them on mount) so a filtered view survives refresh and can be deep-linked.
	 * Off by default; uses `history.replaceState` so it adds no history entries
	 * and stays independent of the router.
	 */
	syncToUrl?: boolean;
}

function useDebouncedValue<T>(value: T, delay: number): T {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);
	return debounced;
}

export function useServerTableState<TParams = OffsetListParams>(
	options: ServerTableStateOptions<TParams> = {},
): ServerTableState<TParams> {
	const {
		pageSize = DEFAULT_PAGE_SIZE,
		debounceMs = SEARCH_DEBOUNCE_MS,
		resetKey,
		syncToUrl = false,
		toParams = offsetListParams as (state: TableSnapshot) => TParams,
	} = options;

	// Restore from the URL in the lazy initialisers, not in an effect: an
	// effect-based restore commits the defaults first, so the sync effect's
	// first run (racing the search debounce) rewrote the URL and dropped `q`.
	const [initialSnapshot] = useState(() =>
		syncToUrl && typeof window !== "undefined"
			? parseTableUrlState(window.location.search, { pageSize })
			: null,
	);
	const [sorting, setSorting] = useState<SortingState>(
		initialSnapshot?.sort
			? [{ desc: initialSnapshot.sort.desc, id: initialSnapshot.sort.id }]
			: [],
	);
	const [globalFilter, setGlobalFilter] = useState(initialSnapshot?.search ?? "");
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: initialSnapshot?.pageIndex ?? 0,
		pageSize: initialSnapshot?.pageSize ?? pageSize,
	});

	const debouncedSearch = useDebouncedValue(globalFilter, debounceMs);
	const sort = sorting[0];

	// Skip the mount run: resetKey is also "changed" on first render, which
	// would clobber a page index just restored from the URL.
	const lastResetKey = useRef(resetKey);
	useEffect(() => {
		if (lastResetKey.current === resetKey) return;
		lastResetKey.current = resetKey;
		setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
	}, [resetKey]);

	const snapshot = useMemo<TableSnapshot>(
		() => ({
			pageIndex: pagination.pageIndex,
			pageSize: pagination.pageSize,
			search: debouncedSearch,
			sort: sort ? { desc: sort.desc, id: sort.id } : undefined,
		}),
		[pagination.pageIndex, pagination.pageSize, sort, debouncedSearch],
	);

	useEffect(() => {
		if (!syncToUrl || typeof window === "undefined") return;
		const query = tableUrlSearch(window.location.search, snapshot, { pageSize });
		const next = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
		if (next === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
			return;
		}
		window.history.replaceState(window.history.state, "", next);
	}, [syncToUrl, snapshot, pageSize]);

	const params = useMemo(() => toParams(snapshot), [toParams, snapshot]);

	const onGlobalFilterChange = useCallback((value: string) => {
		setGlobalFilter(value);
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	}, []);

	const onSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
		setSorting((prev) => (typeof updater === "function" ? updater(prev) : updater));
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	}, []);

	const onPaginationChange = useCallback<OnChangeFn<PaginationState>>((updater) => {
		setPagination((prev) => (typeof updater === "function" ? updater(prev) : updater));
	}, []);

	const tableProps = useCallback(
		(rowCount: number, extras?: { isFetching?: boolean }): ServerTableBind => ({
			sorting,
			onSortingChange,
			globalFilter,
			onGlobalFilterChange,
			pagination,
			onPaginationChange,
			rowCount,
			pageCount: pageCountFromTotal(rowCount, pagination.pageSize),
			isFetching: extras?.isFetching,
		}),
		[
			sorting,
			onSortingChange,
			globalFilter,
			onGlobalFilterChange,
			pagination,
			onPaginationChange,
		],
	);

	return {
		sorting,
		onSortingChange,
		globalFilter,
		onGlobalFilterChange,
		pagination,
		onPaginationChange,
		pageSize,
		params,
		tableProps,
	};
}
